<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useOnboardingDocumentUpload } from '@/composables/useOnboardingDocumentUpload'
import { useProfilePictureUpload } from '@/composables/useProfilePictureUpload'
import HelpSupportCard from '@/Components/Forms/HelpSupportCard.vue'
import BankAccountForm from '@/Components/Forms/BankAccountForm.vue'
import DocumentUploadForm from '@/Components/Forms/DocumentUploadForm.vue'
import ProfileMediaUpload from '@/Components/Forms/ProfileMediaUpload.vue'
import NavigationButtons from '@/Components/Onboarding/NavigationButtons.vue'
import CheckboxField from '@/Components/Forms/CheckboxField.vue'
import LegalModal from '@/Components/Modals/LegalModal.vue'
import TutorTermsContent from '@/Components/Legal/TutorTermsContent.vue'
import PrivacyPolicyContent from '@/Components/Legal/PrivacyPolicyContent.vue'

const props = defineProps({
  slug: {
    type: String,
    required: true
  },
  application: {
    type: Object,
    default: () => null
  }
})

const emit = defineEmits(['complete', 'back'])

// Setup document upload composable
const { uploadDocument, getDownloadUrl } = useOnboardingDocumentUpload(
  props.slug,
  '/become-a-tutor/presigned-upload-url',
  '/become-a-tutor/download-url'
)

// Setup profile picture upload composable
const { uploadProfilePicture, isUploading: isUploadingProfilePicture } = useProfilePictureUpload(
  props.slug,
  '/become-a-tutor/presigned-upload-url'
)

const loading = ref(false)
const localErrors = ref({})

// Bank account data
const bankAccount = ref({
  account_holder_name: '',
  sort_code: '',
  account_number: '',
  bank_name: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  county: '',
  postcode: '',
})

// Document uploads
const documents = ref({
  dbs_document: null,
  identity_document: null,
})

// Profile media (photo and video)
const avatarUrl = ref(null)
const profileVideo = ref({
  upload_id: null,
  playback_id: null
})

const showTermsModal = ref(false)
const showPrivacyModal = ref(false)

// Terms and agreements
const agreements = ref({
  terms_accepted: false,
  payment_confirmation: false,
  marketing_consent: false,
  tax_acknowledgment: false,
})

// Clear errors reactively when fields change
watch(() => bankAccount.value.account_holder_name, () => {
  if (localErrors.value.account_holder_name) {
    delete localErrors.value.account_holder_name
  }
})

watch(() => bankAccount.value.sort_code, () => {
  if (localErrors.value.sort_code) {
    delete localErrors.value.sort_code
  }
})

watch(() => bankAccount.value.account_number, () => {
  if (localErrors.value.account_number) {
    delete localErrors.value.account_number
  }
})

watch(() => bankAccount.value.bank_name, () => {
  if (localErrors.value.bank_name) {
    delete localErrors.value.bank_name
  }
})

watch(() => bankAccount.value.address_line_1, () => {
  if (localErrors.value.address_line_1) {
    delete localErrors.value.address_line_1
  }
})

watch(() => bankAccount.value.city, () => {
  if (localErrors.value.city) {
    delete localErrors.value.city
  }
})

watch(() => bankAccount.value.county, () => {
  if (localErrors.value.county) {
    delete localErrors.value.county
  }
})

watch(() => bankAccount.value.postcode, () => {
  if (localErrors.value.postcode) {
    delete localErrors.value.postcode
  }
})

watch(() => documents.value.dbs_document, () => {
  if (localErrors.value.dbs_document) {
    delete localErrors.value.dbs_document
  }
})

watch(() => documents.value.identity_document, () => {
  if (localErrors.value.identity_document) {
    delete localErrors.value.identity_document
  }
})

watch(() => agreements.value.terms_accepted, () => {
  if (localErrors.value.terms) {
    delete localErrors.value.terms
  }
})

watch(() => agreements.value.payment_confirmation, () => {
  if (localErrors.value.payment) {
    delete localErrors.value.payment
  }
})

watch(() => agreements.value.tax_acknowledgment, () => {
  if (localErrors.value.tax) {
    delete localErrors.value.tax
  }
})

watch(() => avatarUrl.value, () => {
  if (localErrors.value.avatar_url) {
    delete localErrors.value.avatar_url
  }
})

watch(() => profileVideo.value, () => {
  if (localErrors.value.video) {
    delete localErrors.value.video
  }
}, { deep: true })

// Populate form data from application
const populateFromApplication = () => {
  if (!props.application) return

  // Populate bank account data
  if (props.application.bank_account) {
    bankAccount.value = {
      account_holder_name: props.application.bank_account.account_holder_name || '',
      sort_code: props.application.bank_account.sort_code || '',
      account_number: props.application.bank_account.account_number || '',
      bank_name: props.application.bank_account.bank_name || '',
      address_line_1: props.application.bank_account.address_line_1 || '',
      address_line_2: props.application.bank_account.address_line_2 || '',
      city: props.application.bank_account.city || '',
      county: props.application.bank_account.county || '',
      postcode: props.application.bank_account.postcode || '',
    }
  }

  // Populate documents
  if (props.application.documents) {
    documents.value = {
      dbs_document: props.application.documents.dbs || null,
      identity_document: props.application.documents.identity || null,
    }
  }

  // Populate agreements
  if (props.application.agreements) {
    agreements.value = {
      terms_accepted: props.application.agreements.terms_accepted || false,
      payment_confirmation: props.application.agreements.payment_confirmation || false,
      marketing_consent: props.application.agreements.marketing_consent || false,
      tax_acknowledgment: props.application.agreements.tax_acknowledgment || false,
    }
  }

  // Populate avatar URL
  if (props.application.avatar_url) {
    avatarUrl.value = props.application.avatar_url
  }

  // Populate profile video
  if (props.application.profile_video) {
    profileVideo.value = {
      upload_id: props.application.profile_video.upload_id || null,
      playback_id: props.application.profile_video.playback_id || null
    }
  }
}

// Populate on mount and when application changes
onMounted(() => {
  populateFromApplication()
})

watch(() => props.application, () => {
  populateFromApplication()
}, { deep: true })

const validate = () => {
  const errors = {}

  if (!bankAccount.value.account_holder_name.trim()) {
    errors.account_holder_name = 'Account holder name is required'
  }

  const sortCodeDigits = bankAccount.value.sort_code.replace(/[^0-9]/g, '')
  if (sortCodeDigits.length !== 6) {
    errors.sort_code = 'Sort code must be 6 digits'
  }

  if (bankAccount.value.account_number.length !== 8) {
    errors.account_number = 'Account number must be 8 digits'
  }

  if (!bankAccount.value.bank_name) {
    errors.bank_name = 'Please select your bank'
  }

  if (!bankAccount.value.address_line_1.trim()) {
    errors.address_line_1 = 'Address is required'
  }

  if (!bankAccount.value.city.trim()) {
    errors.city = 'City is required'
  }

  if (!bankAccount.value.postcode.trim()) {
    errors.postcode = 'Postcode is required'
  }

  // Validate documents - both are mandatory
  if (!documents.value.dbs_document) {
    errors.dbs_document = 'DBS certificate is required'
  }

  if (!documents.value.identity_document) {
    errors.identity_document = 'Identity document is required'
  }

  // Validate profile picture - required
  if (!avatarUrl.value) {
    errors.avatar_url = 'Profile picture is required'
  }

  if (!agreements.value.terms_accepted) {
    errors.terms = 'You must accept the Terms of Service and Privacy Policy'
  }

  if (!agreements.value.payment_confirmation) {
    errors.payment = 'You must confirm your bank details are accurate'
  }

  if (!agreements.value.tax_acknowledgment) {
    errors.tax = 'You must acknowledge your tax responsibilities'
  }

  localErrors.value = errors
  return Object.keys(errors).length === 0
}

const submitForm = async () => {
  if (!validate()) {
    // Scroll to first error
    const firstError = document.querySelector('.text-red-500')
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    return
  }

  loading.value = true
  localErrors.value = {}

  console.log('💾 Submitting bank details with avatar_url:', avatarUrl.value)

  // Send marketing consent separately (fire and forget)
  if (agreements.value.marketing_consent) {
    try {
      await fetch('/api/marketing-consent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({
          user_id: null,
          email: props.application?.email || '',
          consented: true,
          source: 'tutor_onboarding_bank_details'
        })
      })
    } catch (error) {
      // Silently fail - marketing consent logging is not critical
      console.log('Marketing consent logging failed (non-critical):', error)
    }
  }

  // Prepare form data
  const formData = {
    bank_account: {
      account_holder_name: bankAccount.value.account_holder_name,
      sort_code: bankAccount.value.sort_code,
      account_number: bankAccount.value.account_number,
      bank_name: bankAccount.value.bank_name,
      address_line_1: bankAccount.value.address_line_1,
      address_line_2: bankAccount.value.address_line_2,
      city: bankAccount.value.city,
      county: bankAccount.value.county,
      postcode: bankAccount.value.postcode,
    },
    documents: {
      dbs: documents.value.dbs_document,
      identity: documents.value.identity_document,
    },
    agreements: {
      terms_accepted: agreements.value.terms_accepted,
      payment_confirmation: agreements.value.payment_confirmation,
      marketing_consent: agreements.value.marketing_consent,
      tax_acknowledgment: agreements.value.tax_acknowledgment,
    },
    avatar_url: avatarUrl.value,
    profile_video: profileVideo.value.upload_id ? {
      upload_id: profileVideo.value.upload_id,
      playback_id: profileVideo.value.playback_id
    } : null,
    is_draft: false
  }

  console.log('📦 Form data being sent:', formData)

  try {
    const response = await fetch('/become-a-tutor/bank-details', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Tutor-Slug': props.slug,
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify(formData)
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Map Laravel's nested errors to flat structure for form components
        localErrors.value = Object.keys(data.errors).reduce((acc, key) => {
          // Extract the first error message from array
          const errorMessage = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]

          let flatKey = key

          // Handle bank account fields: 'bank_account.sort_code' -> 'sort_code'
          if (key.startsWith('bank_account.')) {
            flatKey = key.replace('bank_account.', '')
          }
          // Handle document fields: 'documents.dbs' or 'documents.dbs.path' -> 'dbs_document'
          else if (key.startsWith('documents.dbs')) {
            flatKey = 'dbs_document'
          }
          else if (key.startsWith('documents.identity')) {
            flatKey = 'identity_document'
          }
          // Handle agreement fields with specific mappings
          else if (key === 'agreements.terms_accepted') {
            flatKey = 'terms'
          }
          else if (key === 'agreements.payment_confirmation') {
            flatKey = 'payment'
          }
          else if (key === 'agreements.tax_acknowledgment') {
            flatKey = 'tax'
          }
          else if (key === 'agreements.marketing_consent') {
            flatKey = 'marketing'
          }

          acc[flatKey] = errorMessage
          return acc
        }, {})

        // Scroll to first error
        setTimeout(() => {
          const firstError = document.querySelector('.text-red-500')
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 100)
      } else {
        alert(data.message || 'Failed to submit application. Please try again.')
      }
      return
    }

    // Emit complete with application data
    emit('complete', {
      application: data.application,
      slug: data.slug
    })

  } catch (error) {
    console.error('Error submitting application:', error)
    alert('An error occurred while submitting your application. Please try again.')
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  emit('back')
}

const saveDraft = async () => {
  loading.value = true
  localErrors.value = {}

  // Prepare form data
  const formData = {
    bank_account: {
      account_holder_name: bankAccount.value.account_holder_name,
      sort_code: bankAccount.value.sort_code,
      account_number: bankAccount.value.account_number,
      bank_name: bankAccount.value.bank_name,
      address_line_1: bankAccount.value.address_line_1,
      address_line_2: bankAccount.value.address_line_2,
      city: bankAccount.value.city,
      county: bankAccount.value.county,
      postcode: bankAccount.value.postcode,
    },
    documents: {
      dbs: documents.value.dbs_document,
      identity: documents.value.identity_document,
    },
    agreements: {
      terms_accepted: agreements.value.terms_accepted,
      payment_confirmation: agreements.value.payment_confirmation,
      marketing_consent: agreements.value.marketing_consent,
      tax_acknowledgment: agreements.value.tax_acknowledgment,
    },
    avatar_url: avatarUrl.value,
    profile_video: profileVideo.value.upload_id ? {
      upload_id: profileVideo.value.upload_id,
      playback_id: profileVideo.value.playback_id
    } : null,
    is_draft: true
  }

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await fetch('/become-a-tutor/bank-details', {
      method: 'POST',
      headers,
      body: JSON.stringify(formData)
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        localErrors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        localErrors.value = { general: data.message || 'Failed to save draft. Please try again.' }
      }
      loading.value = false
      return
    }

    loading.value = false

    // Show success message
    localErrors.value = { success: 'Draft saved successfully!' }
    setTimeout(() => {
      localErrors.value = {}
    }, 3000)

  } catch (err) {
    localErrors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <div class="grid lg:grid-cols-12 gap-8">
    <!-- Left Sidebar - 4 cols -->
    <aside id="sidebar-info" class="lg:col-span-4">
      <div class="sticky top-32 space-y-6">
        <!-- Intro Card -->
        <section id="intro-card" class="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div class="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <span class="material-symbols-outlined text-white text-3xl">account_balance</span>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 mb-3 text-center">Almost there!</h2>
          <p class="text-gray-600 text-center leading-relaxed">
            Enter your bank details to receive payments from your tutoring sessions securely and efficiently
          </p>
        </section>

        <!-- Security Card -->
        <section id="security-card" class="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-6">
          <div class="flex items-start space-x-4">
            <div class="w-12 h-12 bg-brand-blue rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
              <span class="material-symbols-outlined text-white text-xl">shield</span>
            </div>
            <div>
              <p class="font-semibold text-brand-blue mb-2 text-lg">Secure & Encrypted</p>
              <p class="text-sm text-blue-700 leading-relaxed">
                Your banking information is protected with bank-level 256-bit encryption and never shared with third parties
              </p>
            </div>
          </div>
        </section>

        <!-- Payment Schedule Card -->
        <section id="payment-schedule-card" class="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span class="material-symbols-outlined text-brand-blue mr-2">calendar_month</span>
            Payment Schedule
          </h3>
          <div class="space-y-4">
            <div class="flex items-start space-x-3 p-4 bg-green-50 rounded-xl border border-green-100">
              <div class="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="material-symbols-outlined text-white">event_available</span>
              </div>
              <div>
                <p class="font-semibold text-gray-900 mb-1">Monthly Payments</p>
                <p class="text-sm text-gray-600">Processed on the 1st of each month</p>
              </div>
            </div>
            <div class="flex items-start space-x-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <div class="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="material-symbols-outlined text-white">schedule</span>
              </div>
              <div>
                <p class="font-semibold text-gray-900 mb-1">Processing Time</p>
                <p class="text-sm text-gray-600">1-3 business days to your account</p>
              </div>
            </div>
            <div class="flex items-start space-x-3 p-4 bg-purple-50 rounded-xl border border-purple-100">
              <div class="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span class="material-symbols-outlined text-white">percent</span>
              </div>
              <div>
                <p class="font-semibold text-gray-900 mb-1">Platform Fee</p>
                <p class="text-sm text-gray-600">15% commission on earnings</p>
              </div>
            </div>
          </div>
        </section>

        <!-- Help Card -->
        <HelpSupportCard />
      </div>
    </aside>

    <!-- Main Content Area - 8 cols -->
    <div class="lg:col-span-8 space-y-6">
      <!-- Bank Account Form -->
      <BankAccountForm v-model="bankAccount" :errors="localErrors" />

      <!-- Document Upload Form -->
      <DocumentUploadForm
        v-model="documents"
        :slug="slug"
        :errors="localErrors"
        :upload-document="uploadDocument"
        :get-download-url="getDownloadUrl"
      />

      <!-- Profile Media Upload (Photo & Video) -->
      <ProfileMediaUpload
        :photo="avatarUrl"
        :video="profileVideo"
        :slug="slug"
        :upload-picture-function="uploadProfilePicture"
        :is-uploading-picture="isUploadingProfilePicture"
        :errors="localErrors"
        @update:photo="avatarUrl = $event"
        @update:video="profileVideo = $event"
      />

      <!-- Terms & Agreements Section -->
      <section class="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-10">
        <h3 class="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <span class="material-symbols-outlined text-brand-blue mr-2">description</span>
          Terms & Agreements
        </h3>

        <div class="space-y-0 md:space-y-5">
          <!-- Terms of Service -->
          <CheckboxField
            v-model="agreements.terms_accepted"
            id="terms-check"
            :error="localErrors.terms"
            :required="true"
          >
            I agree to the <button type="button" @click.prevent="showTermsModal = true" class="text-brand-blue font-semibold hover:underline">Terms of Service</button> and <button type="button" @click.prevent="showPrivacyModal = true" class="text-brand-blue font-semibold hover:underline">Privacy Policy</button>. I understand that my personal information will be handled in accordance with applicable data protection laws.
          </CheckboxField>

          <!-- Payment Confirmation -->
          <CheckboxField
            v-model="agreements.payment_confirmation"
            id="payment-check"
            :error="localErrors.payment"
            :required="true"
          >
            I confirm that the bank details provided are accurate and belong to me. I understand that incorrect information may delay payments.
          </CheckboxField>

          <!-- Tax Acknowledgment -->
          <CheckboxField
            v-model="agreements.tax_acknowledgment"
            id="tax-check"
            :error="localErrors.tax"
            :required="true"
          >
            I acknowledge that I am responsible for reporting my earnings to HMRC and paying any applicable taxes on income earned through this platform.
          </CheckboxField>

          <!-- Marketing Consent -->
          <CheckboxField
            v-model="agreements.marketing_consent"
            id="marketing-check"
          >
            I would like to receive updates, tips, and marketing communications from Bindle via email. You can unsubscribe at any time.
          </CheckboxField>
        </div>
      </section>

    </div>
  </div>

  <!-- Navigation Buttons -->
  <NavigationButtons
    :loading="loading"
    :show-previous="true"
    primary-button-text="Submit Application"
    primary-button-loading-text="Submitting..."
    @previous="goBack"
    @save-draft="saveDraft"
    @submit="submitForm"
  />

  <!-- Legal Modals -->
  <LegalModal :show="showTermsModal" title="Tutor Terms & Conditions" @close="showTermsModal = false">
    <TutorTermsContent />
  </LegalModal>

  <LegalModal :show="showPrivacyModal" title="Privacy Policy" @close="showPrivacyModal = false">
    <PrivacyPolicyContent />
  </LegalModal>
</template>
